import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  Chip,
  Box,
  Typography,
  Checkbox,
  Tooltip,
  useTheme
} from '@mui/material';
import { ArrowUpward, ArrowDownward, Lock } from '@mui/icons-material';
import { useCurrency } from '../../context/CurrencyContext';
import { shouldShowLockIcon } from '../../utils/statusHelpers';
import StatusBadge from './StatusBadge';

// Status colors mapping (deprecated in favor of inline logic but keeping keys if needed for reference, 
// though ESLint says it's unused, let's just remove it if it's truly unused)
// statusColors removed because it's unused and has duplicate keys.

const EnhancedTable = ({
  columns = [],
  data = [],
  title,
  defaultSortColumn = null,
  defaultSortDirection = 'desc',
  showPagination = true,
  defaultPageSize = 10,
  pageSizeOptions = [10, 25, 50, 100],
  emptyMessage = 'No records found',
  onRowClick,
  stickyHeader = false,
  maxHeight = 'auto',
  footerData = null,
  actions = null, // Action buttons/menu for each row
  showTotals = false, // Show totals row for visible data
  selectable = false,
  selected = [],
  onSelectedChange = null,
  keyField = 'id',
  titleActions = null,
  serverSide = false,
  serverPage = 0,
  serverRowsPerPage = 10,
  serverCount = 0,
  onServerPageChange = null,
  onServerRowsPerPageChange = null,
  onServerSortChange = null,
  serverSortBy = null,
  serverSortDirection = 'desc',
  rowProps = null, // Function that returns props for each row
  ...props
}) => {
  const theme = useTheme();
  const { formatAmount, formatNumber, baseCurrency, currencies } = useCurrency();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultPageSize);
  const [sortBy, setSortBy] = useState(defaultSortColumn || (columns[0]?.field));
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);

  // Sort data (skip if server-side)
  const sortedData = useMemo(() => {
    if (serverSide || !sortBy || !data.length) return data;

    return [...data].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Handle dates
      if (aVal instanceof Date || bVal instanceof Date ||
        (typeof aVal === 'string' && !isNaN(Date.parse(aVal)))) {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      // Handle numbers
      if (typeof aVal === 'number' || typeof bVal === 'number' ||
        (!isNaN(parseFloat(aVal)) && !isNaN(parseFloat(bVal)))) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }

      // String comparison
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortBy, sortDirection]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!showPagination || serverSide) return sortedData;
    const startIndex = page * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedData, page, rowsPerPage, showPagination, serverSide]);

  // Calculate totals for visible data
  const visibleTotals = useMemo(() => {
    if (!showTotals || !paginatedData.length) return null;

    const totals = {};
    columns.forEach(column => {
      const fieldLower = column.field?.toLowerCase() || '';

      // Exclude note numbers and dates
      const fieldOriginal = column.field || '';
      if (fieldLower.includes('note') || fieldOriginal.toLowerCase().includes('note')) return;
      if (fieldLower.includes('creditnote') || fieldOriginal.toLowerCase().includes('creditnote')) return;
      if (fieldLower.includes('debitnote') || fieldOriginal.toLowerCase().includes('debitnote')) return;
      if (fieldLower.includes('ref') || fieldOriginal.toLowerCase().includes('ref')) return;
      if (column.type === 'date' || fieldLower.includes('date')) return;
      if (column.type === 'status' || fieldLower.includes('status')) return;
      if (column.type === 'reference' || fieldLower.includes('ref')) return;
      // Include currency and amount fields
      if (column.type === 'currency' || fieldLower.includes('amount') ||
        fieldLower.includes('price') || fieldLower.includes('total') ||
        fieldLower.includes('balance') || fieldLower.includes('cost') ||
        fieldLower.includes('paid') || fieldLower.includes('debit') || fieldLower.includes('hand') ||
        fieldLower.includes('credit') || fieldLower.includes('value')) {
        const sum = paginatedData.reduce((acc, row) => {
          let value = parseFloat(row[column.field]) || 0;
          // Handle negative values for credit entries in accounting
          if (fieldLower.includes('credit') &&
            !fieldLower.includes('credit note') &&
            !fieldLower.includes('creditNote') &&
            value > 0) {
            value = -value; // Credits are negative in accounting
          }
          return acc + value;
        }, 0);
        totals[column.field] = sum;
      }
    });

    // Calculate net totals for debit/credit columns
    if (totals.debit !== undefined && totals.credit !== undefined) {
      totals.netBalance = (totals.debit || 0) + (totals.credit || 0);
    }

    return totals;
  }, [paginatedData, columns, showTotals]);

  const handleSort = (field) => {
    if (serverSide) {
      // Use server sort state to determine next direction
      const currentDir = serverSortBy === field ? serverSortDirection : 'desc';
      const newDirection = currentDir === 'asc' ? 'desc' : 'asc';
      onServerSortChange?.(field, newDirection);
    } else {
      const isAsc = sortBy === field && sortDirection === 'asc';
      const newDirection = isAsc ? 'desc' : 'asc';
      setSortDirection(newDirection);
      setSortBy(field);
    }
  };

  const handleChangePage = (event, newPage) => {
    if (serverSide) {
      onServerPageChange?.(event, newPage);
    } else {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    if (serverSide) {
      onServerRowsPerPageChange?.(event);
    } else {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    }
  };

  const currentPageIds = useMemo(
    () => paginatedData.map((n, idx) => n[keyField] || n.id || idx),
    [paginatedData, keyField]
  );

  const numSelectedOnPage = useMemo(
    () => currentPageIds.filter((id) => selected.includes(id)).length,
    [currentPageIds, selected]
  );

  const isAllPageSelected = currentPageIds.length > 0 && numSelectedOnPage === currentPageIds.length;
  const isPageIndeterminate = numSelectedOnPage > 0 && !isAllPageSelected;

  const handleSelectAll = (event) => {
    if (onSelectedChange) {
      if (event.target.checked) {
        const newSelected = Array.from(new Set([...selected, ...currentPageIds]));
        onSelectedChange(newSelected);
      } else {
        const pageIdSet = new Set(currentPageIds);
        const newSelected = selected.filter((id) => !pageIdSet.has(id));
        onSelectedChange(newSelected);
      }
    }
  };

  const handleSelectRow = (event, id) => {
    event.stopPropagation();
    if (onSelectedChange) {
      const selectedIndex = selected.indexOf(id);
      let newSelected = [];

      if (selectedIndex === -1) {
        newSelected = [...selected, id];
      } else {
        newSelected = selected.filter(item => item !== id);
      }

      onSelectedChange(newSelected);
    }
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const isUUID = (str) => typeof str === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str.trim());

  const formatCellValue = (value, column, row, index) => {
    // Custom render function takes precedence
    if (column.renderCell) {
      const rendered = row ? column.renderCell(row, index) : '-';
      if (typeof rendered === 'string' && isUUID(rendered)) return '-';
      return rendered;
    }
    if (column.render) {
      const rendered = row ? column.render(row, index) : '-';
      if (typeof rendered === 'string' && isUUID(rendered)) return '-';
      return rendered;
    }

    // Handle empty or UUID values
    if (value == null || value === '' || isUUID(value)) {
      return '-';
    }

    // Date formatting
    if (column.type === 'date' || column.field?.toLowerCase().includes('date')) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
      }
      return value;
    }

    // Currency formatting
    const fieldLower = column.field?.toLowerCase() || '';
    const headerLower = column.headerName?.toLowerCase() || '';
    const isCurrencyCol = column.type === 'currency' ||
      fieldLower.includes('amount') || fieldLower.includes('price') ||
      fieldLower.includes('total') || fieldLower.includes('balance') ||
      fieldLower.includes('cost') || fieldLower.includes('rate') ||
      fieldLower.includes('fee') || fieldLower.includes('duty') ||
      fieldLower.includes('salary') || fieldLower.includes('debit') ||
      fieldLower.includes('credit') || fieldLower.includes('budget') ||
      headerLower.includes('amount') || headerLower.includes('price') ||
      headerLower.includes('total') || headerLower.includes('balance') ||
      headerLower.includes('cost') || headerLower.includes('rate');

    if (isCurrencyCol) {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        // Resolve row currency & exchange rate
        const rawCurrency = (row && (row.currency || row.Currency)) ||
          (row && (row.currencyId || row.currency_id || row.transactionCurrencyId || row.originalCurrencyId) &&
            currencies?.find(c => c.id === (row.currencyId || row.currency_id || row.transactionCurrencyId || row.originalCurrencyId))) ||
          (column && (column.currency || (column.currencyCode && { code: column.currencyCode }))) ||
          null;

        let currencyCode = null;
        let currencySymbol = null;
        if (typeof rawCurrency === 'string') {
          currencyCode = rawCurrency.toUpperCase();
        } else if (rawCurrency && typeof rawCurrency === 'object') {
          currencyCode = (rawCurrency.code || rawCurrency.currencyCode || '').toUpperCase();
          currencySymbol = rawCurrency.symbol || null;
        } else if (row && (row.currencyCode || row.currency_code)) {
          currencyCode = (row.currencyCode || row.currency_code).toUpperCase();
        }

        const resolvedBaseCode = (baseCurrency?.code || 'USD').toUpperCase();
        const baseSymbol = baseCurrency?.symbol || (resolvedBaseCode === 'USD' ? '$' : resolvedBaseCode);

        if (!currencyCode) {
          currencyCode = resolvedBaseCode;
        }

        // Live currency record from active currencies list
        const liveCurrency = currencies?.find(c =>
          (c.code && currencyCode && c.code.toUpperCase() === currencyCode) ||
          (row?.currencyId && c.id === row.currencyId) ||
          (row?.currency_id && c.id === row.currency_id) ||
          (row?.transactionCurrencyId && c.id === row.transactionCurrencyId) ||
          (row?.originalCurrencyId && c.id === row.originalCurrencyId)
        );
        const currentExchangeRate = parseFloat(liveCurrency?.exchangeRate);

        // Stored exchange rate used by system for this transaction
        const rowRawRate = row?.exchangeRate ?? row?.exchange_rate ?? row?.rate ?? row?.fxRate;
        const rowRate = parseFloat(rowRawRate);

        const isBaseCurr = currencyCode === resolvedBaseCode;

        // Display string in cell
        const sym = currencySymbol || liveCurrency?.symbol || (isBaseCurr ? baseSymbol : currencyCode);
        const formattedAmount = `${sym} ${numValue.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}`;

        // Construct Hover Tooltip
        let tooltipTitle = '';

        if (!isBaseCurr) {
          // Foreign currency -> Convert to Base Currency
          const storedFunctionalRaw = row?.functionalTotalAmount ?? row?.baseTotalAmount ??
            row?.functionalAmount ?? row?.baseAmount ?? null;
          const storedFunctional = storedFunctionalRaw != null ? parseFloat(storedFunctionalRaw) : null;

          let baseEquivalent;
          if (storedFunctional != null && !isNaN(storedFunctional) && (Math.abs(storedFunctional - numValue) > 0.0001 || !currentExchangeRate || currentExchangeRate === 1)) {
            baseEquivalent = storedFunctional;
          } else if (!isNaN(rowRate) && rowRate > 0 && rowRate !== 1.0) {
            baseEquivalent = numValue / rowRate;
          } else if (!isNaN(currentExchangeRate) && currentExchangeRate > 0) {
            baseEquivalent = numValue / currentExchangeRate;
          } else if (storedFunctional != null && !isNaN(storedFunctional)) {
            baseEquivalent = storedFunctional;
          } else {
            const fallbackRate = !isNaN(rowRate) && rowRate > 0 ? rowRate : 1.0;
            baseEquivalent = numValue / fallbackRate;
          }

          const effectiveRate = (!isNaN(rowRate) && rowRate > 0 && rowRate !== 1.0) ? rowRate : currentExchangeRate;
          const rateInfo = (effectiveRate && effectiveRate > 0 && effectiveRate !== 1.0)
            ? ` • Rate: 1 ${resolvedBaseCode} = ${effectiveRate.toFixed(effectiveRate < 1 ? 4 : 2)} ${currencyCode}`
            : '';

          tooltipTitle = `≈ ${baseSymbol}${baseEquivalent.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })} ${resolvedBaseCode}${rateInfo}`;
        } else {
          // Base Currency -> Provide conversion to USD or active secondary currency
          if (resolvedBaseCode !== 'USD') {
            const usdCurr = currencies?.find(c => c.code?.toUpperCase() === 'USD');
            const usdRate = parseFloat(usdCurr?.exchangeRate);
            const baseRateAgainstUsd = currentExchangeRate || 1;

            let usdEquivalent;
            if (usdRate && !isNaN(usdRate) && usdRate > 0) {
              usdEquivalent = usdRate > 1 ? (numValue / usdRate) : (numValue * usdRate);
            } else if (baseRateAgainstUsd > 1) {
              usdEquivalent = numValue / baseRateAgainstUsd;
            } else {
              usdEquivalent = numValue;
            }

            tooltipTitle = `≈ $${usdEquivalent.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })} USD • Base: ${resolvedBaseCode}`;
          } else {
            const secondaryCurr = currencies?.find(c => c.code?.toUpperCase() !== 'USD' && !c.isBaseCurrency && parseFloat(c.exchangeRate) > 0);
            if (secondaryCurr) {
              const secRate = parseFloat(secondaryCurr.exchangeRate);
              const secSym = secondaryCurr.symbol || secondaryCurr.code;
              const secCode = secondaryCurr.code.toUpperCase();
              const secEquivalent = numValue * secRate;
              tooltipTitle = `≈ ${secSym} ${secEquivalent.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })} ${secCode} • Base: USD`;
            } else {
              tooltipTitle = `Base Currency: US Dollar (USD)`;
            }
          }
        }

        return (
          <Tooltip title={tooltipTitle} arrow placement="top" enterDelay={50}>
            <Typography
              variant="body2"
              component="span"
              sx={{
                fontWeight: 800,
                color: 'secondary.main',
                cursor: 'help',
                borderBottom: '1px dotted currentColor',
                display: 'inline-block'
              }}
            >
              {formattedAmount}
            </Typography>
          </Tooltip>
        );
      }
      return value;
    }

    // Numeric formatting (right-aligned)
    if (column.type === 'number') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        return (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 800,
              color: 'secondary.main'
            }}
          >
            {formatNumber(numValue)}
          </Typography>
        );
      }
    }

    // Status/type fields as chips
    if (column.type === 'status' || column.field?.toLowerCase().includes('status') ||
      column.field?.toLowerCase().includes('type') || column.field?.toLowerCase().includes('state') ||
      column.field?.toLowerCase().includes('onpayroll')) {
      const isLocked = shouldShowLockIcon(row);
      
      // Handle boolean and diverse string values
      let statusKey = (value || '').toString().toLowerCase();

      if (typeof value === 'boolean') {
        statusKey = value ? 'active' : 'inactive';
      } else if (statusKey === 'yes' || statusKey === 'true') {
        statusKey = 'active';
      } else if (statusKey === 'no' || statusKey === 'false') {
        statusKey = 'inactive';
      }

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <StatusBadge 
            status={row._isDeleting ? 'cancelled' : statusKey} 
            label={row._isDeleting ? 'Deleting' : undefined}
            isLoading={row.isOptimisticProcessing} 
          />
          {isLocked && <Lock fontSize="small" sx={{ color: 'secondary.main', opacity: 0.7 }} titleAccess="This record is locked" />}
        </Box>
      );
    }

    if (typeof value === 'string' && isUUID(value)) {
      return '-';
    }
    return value;
  };

  const getCellAlignment = (column) => {
    if (column.align) return column.align;

    // Right-align numeric and currency columns
    if (column.type === 'number' || column.type === 'currency' ||
      column.field?.toLowerCase().includes('amount') ||
      column.field?.toLowerCase().includes('price') ||
      column.field?.toLowerCase().includes('total') ||
      column.field?.toLowerCase().includes('balance') ||
      column.field?.toLowerCase().includes('cost') ||
      column.field?.toLowerCase().includes('quantity')) {
      return 'right';
    }

    return 'left';
  };

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: '16px',
        overflow: 'hidden',
        maxWidth: '100%',
        width: '100%',
        ...props.sx
      }}
    >
      {title && (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="700">{title}</Typography>
          {titleActions && <Box sx={{ display: 'flex', gap: 1 }}>{titleActions}</Box>}
        </Box>
      )}

      <TableContainer sx={{ maxHeight: maxHeight, overflowX: 'auto', width: '100%' }}>
        <Table stickyHeader={stickyHeader} sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell
                  padding="checkbox"
                  sx={{
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(159, 232, 112, 0.05)' : 'rgba(22, 51, 0, 0.06)',
                    borderBottom: theme.palette.mode === 'dark' ? '2px solid rgba(159, 232, 112, 0.12)' : '2px solid rgba(22, 51, 0, 0.12)',
                  }}
                >
                  <Checkbox
                    indeterminate={isPageIndeterminate}
                    checked={isAllPageSelected}
                    onChange={handleSelectAll}
                    color="primary"
                    size="small"
                  />
                </TableCell>
              )}
              {columns.map((column, colIdx) => {
                const colField = column.field || column.id || `col_${colIdx}`;
                const colHeader = column.headerName || column.label || column.title || colField;
                return (
                  <TableCell
                    key={colField}
                    align={getCellAlignment(column)}
                    sortDirection={sortBy === colField ? sortDirection : false}
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.8125rem',
                      letterSpacing: '0.02em',
                      color: 'secondary.main',
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(159, 232, 112, 0.05)' : 'rgba(22, 51, 0, 0.06)',
                      borderBottom: theme.palette.mode === 'dark' ? '2px solid rgba(159, 232, 112, 0.12)' : '2px solid rgba(22, 51, 0, 0.12)',
                    }}
                  >
                    {column.sortable !== false ? (
                      <TableSortLabel
                        active={serverSide ? serverSortBy === colField : sortBy === colField}
                        direction={serverSide
                          ? (serverSortBy === colField ? serverSortDirection : 'desc')
                          : (sortBy === colField ? sortDirection : 'asc')}
                        onClick={() => handleSort(colField)}
                        IconComponent={
                          (serverSide ? serverSortBy === colField && serverSortDirection === 'desc' : sortBy === colField && sortDirection === 'desc')
                            ? ArrowDownward : ArrowUpward
                        }
                      >
                        {colHeader}
                      </TableSortLabel>
                    ) : (
                      colHeader
                    )}
                  </TableCell>
                );
              })}
              {actions && (
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.8125rem',
                    letterSpacing: '0.02em',
                    color: 'secondary.main',
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(159, 232, 112, 0.05)' : 'rgba(22, 51, 0, 0.06)',
                    borderBottom: theme.palette.mode === 'dark' ? '2px solid rgba(159, 232, 112, 0.12)' : '2px solid rgba(22, 51, 0, 0.12)',
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + (actions ? 1 : 0)} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => {
                const rowKey = row[keyField] || row.id || index;
                const isItemSelected = isSelected(rowKey);
                const additionalRowProps = rowProps ? rowProps(row) : {};
                return (
                  <TableRow
                    key={rowKey}
                    hover
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    selected={isItemSelected}
                    {...additionalRowProps}
                    sx={{
                      cursor: (onRowClick && !row.isOptimisticProcessing && !row._isDeleting) ? 'pointer' : 'default',
                      backgroundColor: index % 2 === 0 ? 'transparent' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : theme.palette.grey[50]),
                      opacity: (row.isOptimisticProcessing || row._isDeleting) ? 0.6 : 1,
                      pointerEvents: (row.isOptimisticProcessing || row._isDeleting) ? 'none' : 'auto',
                      transition: 'opacity 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: (row.isOptimisticProcessing || row._isDeleting) ? 'transparent' : theme.palette.action.hover,
                      },
                      '& td': {
                        whiteSpace: { xs: 'nowrap', md: 'normal' },
                        overflow: { xs: 'hidden', md: 'visible' },
                        textOverflow: { xs: 'ellipsis', md: 'clip' }
                      },
                      ...additionalRowProps.sx
                    }}
                  >
                    {selectable && (
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isItemSelected}
                          onChange={(event) => handleSelectRow(event, row[keyField] || row.id || index)}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                    )}
                    {columns.map((column, cIdx) => {
                      const colField = column.field || column.id || `col_${cIdx}`;
                      return (
                        <TableCell
                          key={colField}
                          align={getCellAlignment(column)}
                        >
                          {formatCellValue(row[colField], column, row, index)}
                        </TableCell>
                      );
                    })}
                    {actions && (
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          {typeof actions === 'function' ? actions(row) : actions}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}

            {/* Footer row for totals */}
            {(footerData || visibleTotals) && (
              <TableRow sx={{
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : theme.palette.grey[100],
                '& td': { fontWeight: 600, borderTop: 2, borderColor: 'divider' }
              }}>
                {selectable && <TableCell />}
                {columns.map((column) => {
                  const fieldLower = column.field?.toLowerCase() || '';
                  const totalValue = footerData?.[column.field] ?? visibleTotals?.[column.field];

                  // Don't show totals for note numbers
                  const fieldOriginal = column.field || '';
                  const isNoteNumber = fieldLower.includes('note') || fieldOriginal.toLowerCase().includes('note') ||
                    fieldLower.includes('creditnote') || fieldOriginal.toLowerCase().includes('creditnote') ||
                    fieldLower.includes('debitnote') || fieldOriginal.toLowerCase().includes('debitnote') ||
                    fieldLower.includes('ref') || fieldOriginal.toLowerCase().includes('ref');

                  return (
                    <TableCell
                      key={`footer-${column.field}`}
                      align={getCellAlignment(column)}
                    >
                      {!isNoteNumber && totalValue != null ? (
                        formatCellValue(totalValue, { ...column, type: 'currency' }, footerData || {})
                      ) : (
                        column.field === columns[0].field ? (
                          <Typography variant="body2" fontWeight="bold" color={theme.palette.mode === 'dark' ? 'text.primary' : 'inherit'}>
                            {showTotals ? `Total (${paginatedData.length} rows)` : 'Total'}
                          </Typography>
                        ) : ''
                      )}
                    </TableCell>
                  );
                })}
                {actions && <TableCell />}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {showPagination && (
        <TablePagination
          rowsPerPageOptions={pageSizeOptions}
          component="div"
          count={serverSide ? serverCount : sortedData.length}
          rowsPerPage={serverSide ? serverRowsPerPage : rowsPerPage}
          page={serverSide ? serverPage : page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      )}
    </Paper>
  );
};

export default EnhancedTable;
